import { replayEnd } from "./accounts.ts"
import { accountsStore, accountStore, syncChartPreference } from "./tradingData.ts"

// Where a chart following a replay stops (docs/design/0005, ReplayCursor): the
// followed account's virtual time, when it is a Replay account and "Sync chart
// to replay" is on. ChartPane hands the end to its CandleFeed (`setEnd`), so the
// chart never shows a bar that closed after the replay's now.

/** How long a chart waits to learn whether it follows a replay before it loads unclipped anyway. */
const KNOWN_AFTER_MS = 1_500

/**
 * Follows the replay time for one chart. `known` turns true once the account
 * list has loaded (or after a short wait), so a reloaded page opens its chart at
 * the replay time instead of flashing the present first.
 */
export class ReplayCursor {
  private end: number | null = null
  private known = false
  private readonly onChange: (end: number | null) => void
  private readonly onKnown: () => void
  private readonly stops: ReadonlyArray<() => void>
  private readonly timer: ReturnType<typeof setTimeout>

  constructor(onChange: (end: number | null) => void, onKnown: () => void) {
    this.onChange = onChange
    this.onKnown = onKnown
    this.stops = [
      accountStore.subscribe(this.update),
      accountsStore.subscribe(this.update),
      syncChartPreference.subscribe(this.update)
    ]
    this.timer = setTimeout(() => this.markKnown(), KNOWN_AFTER_MS)
    this.update()
  }

  /** The current end (null: the chart isn't clipped). */
  current(): number | null {
    return this.end
  }

  isKnown(): boolean {
    return this.known
  }

  dispose(): void {
    clearTimeout(this.timer)
    for (const stop of this.stops) stop()
  }

  private readonly update = (): void => {
    const accounts = accountsStore.getState()
    const end = replayEnd(accountStore.getState(), accounts, syncChartPreference.get() === "on")
    if (end !== this.end) {
      this.end = end
      this.onChange(end)
    }
    if (accounts.status === "ready" || accounts.status === "failed") this.markKnown()
  }

  private markKnown(): void {
    if (this.known) return
    this.known = true
    this.onKnown()
  }
}
